import { FormEvent, useState } from "react"
import { Head, Link, router, usePage } from "@inertiajs/react"

import { AdminShell } from "@/components/AdminShell"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { PlusCircle, Trash2, Sparkles, Loader2 } from "lucide-react"
import type { PageProps } from "@/types/inertia"

type AnswerField = {
  id:      number | null
  content: string
  correct: boolean
  _destroy?: boolean
}

type QuestionData = {
  id:       number | null
  content:  string
  more_info: string
  answers:  AnswerField[]
}

type Props = { question: QuestionData }

export default function AdminQuestionForm({ question }: Props) {
  const { props } = usePage<PageProps>()
  const errors = props.errors ?? {}
  const isNew = question.id === null

  const [answers, setAnswers] = useState<AnswerField[]>(
    question.answers.length > 0
      ? question.answers
      : [
          { id: null, content: "", correct: true  },
          { id: null, content: "", correct: false },
          { id: null, content: "", correct: false },
          { id: null, content: "", correct: false },
        ]
  )
  const [processing, setProcessing] = useState(false)
  const [content, setContent]   = useState(question.content)
  const [moreInfo, setMoreInfo] = useState(question.more_info ?? "")
  const [suggesting, setSuggesting]     = useState(false)
  const [suggestError, setSuggestError] = useState<string | null>(null)

  const setCorrect = (index: number) => {
    setAnswers(prev => prev.map((a, i) => ({ ...a, correct: i === index })))
  }

  const updateAnswer = (index: number, field: keyof AnswerField, value: string | boolean) => {
    setAnswers(prev => prev.map((a, i) => i === index ? { ...a, [field]: value } : a))
  }

  const addAnswer = () => {
    setAnswers(prev => [ ...prev, { id: null, content: "", correct: false } ])
  }

  const removeAnswer = (index: number) => {
    setAnswers(prev => {
      const updated = prev.map((a, i) => i === index ? { ...a, _destroy: true } : a)
      // if removed answer was correct, make first surviving answer correct
      const wasCorrect = prev[index].correct
      if (wasCorrect) {
        const firstAlive = updated.findIndex(a => !a._destroy)
        if (firstAlive >= 0) updated[firstAlive] = { ...updated[firstAlive], correct: true }
      }
      return updated
    })
  }

  const suggestMoreInfo = async () => {
    const correctAnswer = answers.find(a => a.correct && !a._destroy)
    if (!content.trim() || !correctAnswer?.content.trim()) return

    setSuggesting(true)
    setSuggestError(null)

    try {
      const csrf = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ""
      const otherAnswers = visibleAnswers
        .filter(a => a !== correctAnswer)
        .map(a => a.content)
        .filter(Boolean)

      const res = await fetch("/admin/questions/suggest_more_info", {
        method:  "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrf, "Accept": "application/json" },
        body:    JSON.stringify({ content, correct_answer: correctAnswer.content, other_answers: otherAnswers }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || `Server error ${res.status}`)
      setMoreInfo(data.more_info)
    } catch (e) {
      setSuggestError(e instanceof Error ? e.message : "Could not generate a suggestion. Please try again.")
    } finally {
      setSuggesting(false)
    }
  }

  const submit = (e: FormEvent) => {
    e.preventDefault()
    setProcessing(true)

    const data = {
      question: {
        content:   content,
        more_info: moreInfo,
        answers_attributes: answers.map(a => ({
          ...(a.id !== null ? { id: a.id } : {}),
          content: a.content,
          correct: a.correct,
          ...(a._destroy ? { _destroy: true } : {}),
        })),
      },
    }

    const opts = { onFinish: () => setProcessing(false) }
    if (isNew) {
      router.post("/admin/questions", data, opts)
    } else {
      router.patch(`/admin/questions/${question.id}`, data, opts)
    }
  }

  const visibleAnswers = answers.filter(a => !a._destroy)

  return (
    <>
      <Head title={isNew ? "New Question — Admin" : "Edit Question — Admin"}>
        <meta name="description" content="Manage trivia question." />
        <meta property="og:title" content={isNew ? "New Question" : "Edit Question"} />
        <meta property="og:description" content="Manage trivia question." />
      </Head>
      <AdminShell>
        <div className="flex items-center justify-between mb-6">
          <h1>{isNew ? "New question" : "Edit question"}</h1>
          <Link href="/admin/questions" className="text-sm text-ink-muted no-underline hover:text-ink-display">
            ← Back to questions
          </Link>
        </div>

        <form onSubmit={submit} className="max-w-2xl space-y-8">
          {/* Question content */}
          <div className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="content">Question</label>
              <textarea
                id="content"
                rows={3}
                className="w-full rounded-lg border border-hairline bg-surface px-3 py-2 text-sm text-ink-display placeholder-ink-muted focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none"
                placeholder="What is the real name of…?"
                value={content}
                onChange={e => setContent(e.target.value)}
                required
              />
              {errors["question.content"] && (
                <p className="text-xs text-danger-display">{errors["question.content"]}</p>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label htmlFor="more_info">More info <span className="text-ink-muted font-normal">(optional — shown after answering)</span></label>
                <button
                  type="button"
                  onClick={suggestMoreInfo}
                  disabled={suggesting || !content.trim() || !answers.some(a => a.correct && !a._destroy && a.content.trim())}
                  className="flex items-center gap-1.5 text-sm text-ink-muted hover:text-ink-display transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Suggest more info with AI"
                >
                  {suggesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                  Suggest
                </button>
              </div>
              <textarea
                id="more_info"
                rows={2}
                className="w-full rounded-lg border border-hairline bg-surface px-3 py-2 text-sm text-ink-display placeholder-ink-muted focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none"
                placeholder="Additional context shown after the answer is revealed…"
                value={moreInfo}
                onChange={e => setMoreInfo(e.target.value)}
              />
              {suggestError && (
                <p className="text-xs text-danger-display">{suggestError}</p>
              )}
            </div>
          </div>

          {/* Answers */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block">Answers <span className="text-ink-muted font-normal">(select the correct one)</span></label>
            </div>

            <div className="space-y-2">
              {answers.map((a, i) => {
                if (a._destroy) return null
                const visibleIndex = visibleAnswers.indexOf(a)
                return (
                  <div key={i} className={`flex items-center gap-3 rounded-lg border px-3 py-2 transition-colors ${a.correct ? "border-green-400 bg-green-50 dark:bg-green-950/20" : "border-hairline bg-surface"}`}>
                    <input
                      type="radio"
                      name="correct_answer"
                      checked={a.correct}
                      onChange={() => setCorrect(i)}
                      className="accent-green-500 shrink-0"
                      title="Mark as correct answer"
                    />
                    <Input
                      value={a.content}
                      onChange={e => updateAnswer(i, "content", e.target.value)}
                      placeholder={`Answer ${visibleIndex + 1}`}
                      className="flex-1 border-0 bg-transparent p-0 shadow-none focus-visible:ring-0 text-sm"
                    />
                    {visibleAnswers.length > 2 && (
                      <button
                        type="button"
                        onClick={() => removeAnswer(i)}
                        className="p-1 text-ink-muted hover:text-red-500 transition-colors shrink-0"
                        title="Remove answer"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                )
              })}
            </div>

            {visibleAnswers.length < 6 && (
              <button
                type="button"
                onClick={addAnswer}
                className="mt-3 flex items-center gap-1.5 text-sm text-ink-muted hover:text-ink-display transition-colors"
              >
                <PlusCircle className="w-4 h-4" />
                Add another answer
              </button>
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="submit" disabled={processing}>
              {isNew ? "Create question" : "Save changes"}
            </Button>
            <Link href="/admin/questions">
              <Button type="button" variant="secondary">Cancel</Button>
            </Link>
          </div>
        </form>
      </AdminShell>
    </>
  )
}
